import { combineReducers } from 'redux'
import { browserHistory } from 'utils/navigation'
import request, { generateNotyMessage } from 'utils/request'
import { actions as notyActions } from 'layouts/ErrorBox'
import { modalActions } from 'store/modal'
import { loadingActions } from 'store/loader'
import { fetchUser } from 'store/user'
import { logSentryEvent } from 'utils/sentry'
import {
  isDeployRegionChina,
  DEFAULT_EMAIL_CONFIG,
  DEFAULT_INSIGHTS_DASHBOARD_CONFIG,
  DEFAULT_STUDY,
  getDefaultStudyConfigForNewStudy,
  MODAL_CLASSES_MAP,
  MODAL_CONTENT_MAP,
  REWARD_TYPE_MAP,
  SECURITY_KEY_MAP,
  DEFAULT_NAME_CONFIG,
} from 'utils/constants'
import { isFunction, isRootOrDchAdmin } from 'utils/misc'
import {
  generateCycles,
  getCyclesInfo,
  generateDynamicCycles,
  getDynamicCyclesInfo,
  formatStudyVersionList,
  isStudyVersionInList,
} from '../utils/studyUtils'
import { CREATE_STUDY_ERROR_PREFIX } from '../utils/constants'
import {
  locales,
  localesMap,
  fetchLocales,
} from '../../Study/routes/Participants/routes/CreateParticipant/modules/CreateParticipant'
import {
  checkIsEmailless,
  getIrtControlledVisitsConfig,
  getStudyVersionID,
  syncCaregiversVersionWithStudyPromotion,
} from '../../../../../utils/studyConfigUtils'
import { SECONDS_IN_MINUTE } from '../../../../../utils/constants'

// Actions/reducers for Create Study Page

const UPDATE_STUDY = 'UPDATE_STUDY'
const UPDATE_CYCLE = 'UPDATE_CYCLE'
const UPDATE_CONFIG = 'UPDATE_CONFIG'
const ADD_CYCLE = 'ADD_CYCLE'
const DELETE_CYCLE = 'DELETE_CYCLE'
const INITIALIZE_BLANK_STUDY = 'INITIALIZE_BLANK_STUDY'
const TOGGLE_MODULE = 'TOGGLE_MODULE'
const SET_STUDY = 'SET_STUDY'
const SET_INSIGHTS_LOW_FUNCTIONALITY_MODE = 'SET_INSIGHTS_LOW_FUNCTIONALITY_MODE'
const RESET_INSIGHTS_LOW_FUNCTIONALITY_MODE = 'RESET_INSIGHTS_LOW_FUNCTIONALITY_MODE'
const UPDATE_STUDY_CUSTOM_LOGO = 'UPDATE_STUDY_CUSTOM_LOGO'
const SAVE_ATTEMPTED = 'SAVE_ATTEMPTED'
const UPDATE_AUTO_NOTIFICATION = 'UPDATE_AUTO_NOTIFICATION'
const SET_ORPHANED_PARTICIPANTS = 'SET_ORPHANED_PARTICIPANTS'
const TOGGLE_LANGUAGE = 'TOGGLE_LANGUAGE'
const SET_STUDY_VERSION_LIST = 'SET_STUDY_VERSION_LIST'
const RESET_STUDY_VERSION_LIST = 'RESET_STUDY_VERSION_LIST'
const SET_CREATE_STUDY_ERROR = 'SET_CREATE_STUDY_ERROR'
const CLEAR_CREATE_STUDY_ERROR = 'CLEAR_CREATE_STUDY_ERROR'
export const NOTIFICATION_CONSTS = {
  UPDATE_AUTO_NOTIFICATION,
}

// Constants

export const requiredFields = ['reward_goal', 'reward_duration']

export const requiredCycleFields = ['target', 'reward', 'cycleDuration', 'studyDuration', 'rewardType']

export const requiredDynamicCycleFields = ['target', 'reward', 'cycleDuration', 'rewardType']

// These keys were moved into the config object in v3.17
export const legacyKeys = [
  'modules',
  'forced_instrument_priority',
  'reward_duration',
  'reward_goal',
  'has_dynamic_cycles',
  'cycles',
  'auto_notification',
  'session_timeout_seconds', // optional
  'is_test', // optional,
  'consent_required', // optional,
]

const advancedStudyConfigKeys = [
  'custom_onboarding',
  'case_management',
  'custom_geofence_behavior',
  'custom_map_button',
]

// Actions

export const resetStudy = () => {
  return {
    type: INITIALIZE_BLANK_STUDY,
    value: DEFAULT_STUDY,
  }
}

const updateStudy = (key, value) => {
  return {
    type: UPDATE_STUDY,
    key,
    value,
  }
}

const updateConfig = value => {
  return {
    type: UPDATE_CONFIG,
    value,
  }
}

let studyVersionListFetchGeneration = 0

export const setStudyVersionList = (list, studyId = null) => {
  return {
    type: SET_STUDY_VERSION_LIST,
    value: list,
    studyId,
  }
}

export const resetStudyData = (creatorIsRootOrDchAdmin = false) => {
  return dispatch => {
    dispatch(resetStudy())
    dispatch(updateConfig(getDefaultStudyConfigForNewStudy(creatorIsRootOrDchAdmin)))
  }
}

const updateCycle = (cycleIdx, key, value) => {
  return {
    type: UPDATE_CYCLE,
    idx: cycleIdx,
    key,
    value,
  }
}

const addCycle = () => {
  return {
    type: ADD_CYCLE,
  }
}

const deleteCycle = idx => {
  return {
    type: DELETE_CYCLE,
    idx,
  }
}

const updateRewards = (cycles, duration, goal) => {
  return updateStudy(['cycles', 'reward_duration', 'reward_goal'], [cycles, duration, goal])
}

const toggleModule = key => {
  return {
    type: TOGGLE_MODULE,
    key,
  }
}

const toggleLanguage = language => {
  return {
    type: TOGGLE_LANGUAGE,
    language,
  }
}

export const resetInsightsLowFunctionalityMode = () => {
  return { type: RESET_INSIGHTS_LOW_FUNCTIONALITY_MODE }
}

const applyCBs = (_study, beforeCreateCbs) => {
  return beforeCreateCbs.reduce((acc, { cb }) => isFunction(cb) && cb(acc), _study)
}

// Thunk actions

const _saveStudy = (study, isPatch = false, studyID = '', diffIsConfig = false) => {
  const user = JSON.parse(localStorage.getItem('user'))
  return dispatch => {
    const customFail = (response, content) => {
      const errorMessage = content?.message || 'Error creating study'
      logSentryEvent({
        message: 'Create/Patch study request failed',
        level: 'error',
        tags: {
          module: 'create-study',
          action: isPatch ? 'patchStudy' : 'createStudy',
          'http.status': response?.status,
        },
        contexts: {
          study: {
            studyID,
            errorMessage,
          },
        },
      })
      const fullErrorMessage = `${CREATE_STUDY_ERROR_PREFIX}${errorMessage}`
      dispatch({ type: SET_CREATE_STUDY_ERROR, error: errorMessage })
      dispatch(notyActions.showError({ text: generateNotyMessage(fullErrorMessage, false) }))
    }
    return dispatch(
      request({
        method: isPatch ? 'PATCH' : 'POST',
        url: isPatch ? `/control/studies/${studyID}` : '/control/studies',
        body: JSON.stringify({ study }),
        failMessage: 'Error creating study',
        successMessage: `Study successfully ${isPatch ? ' saved' : ' created'}`,
        fail: customFail,
        success: () => {
          dispatch({ type: CLEAR_CREATE_STUDY_ERROR })
          dispatch(fetchUser(user, !isPatch))
          if (diffIsConfig) dispatch(loadingActions.stopLoader(true, 'studyConfig'))
          return dispatch(isPatch ? fetchStudy(studyID) : () => { })
        },
      }),
    )
  }
}

export const createStudy = (_study, beforeCreate) => {
  return (dispatch, getState) => {
    dispatch({ type: SAVE_ATTEMPTED })
    dispatch({ type: CLEAR_CREATE_STUDY_ERROR })
    const hasStudyError = _hasStudyError(_study)
    const beforeCreateCbs = Array.isArray(beforeCreate) ? beforeCreate : []
    const sectionError = beforeCreateCbs.map(({ hasErrors }) => isFunction(hasErrors) && hasErrors(_study)).some(e => e)
    if (hasStudyError || sectionError) {
      return dispatch(
        notyActions.showError({ text: generateNotyMessage('Please correct empty fields marked in red.', false) }),
      )
    }
    dispatch(loadingActions.startLoader(true))
    const study = _convertStudyForSave(applyCBs(_study, beforeCreateCbs))
    return dispatch(_saveStudy(study)).then(() => {
      const { user } = getState()
      dispatch(resetStudyData(isRootOrDchAdmin(user)))
      browserHistory.push('/studies')
      return dispatch(loadingActions.stopLoader(true))
    })
  }
}

export const fetchStudy = id => {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: `/control/studies/${id}`,
        errorMessage: `error fetching study ${id}`,
        success: json => {
          _formatStudy(json, dispatch)
          dispatch({
            type: SET_STUDY,
            study: json,
          })
          return Promise.resolve(json)
        },
        hasLoader: false,
        loadingKey: 'studyConfig',
      }),
    )
  }
}

export const fetchStudyInsightsStatus = id => {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: `/control/insights/study_setup/${id}`,
        errorMessage: `error fetching study ${id} insights`,
        success: json => {
          return Promise.resolve(json)
        },
        fail: () => {
          dispatch({
            type: SET_INSIGHTS_LOW_FUNCTIONALITY_MODE,
          })
        },
        hasLoader: true,
        loadingKey: 'insights',
      }),
    )
  }
}

export const fetchStudyVersionList = studyId => {
  return dispatch => {
    const fetchGeneration = ++studyVersionListFetchGeneration

    const fail = (_, content) => {
      if (fetchGeneration !== studyVersionListFetchGeneration) return

      logSentryEvent({
        message: 'Fetch study version list failed',
        level: 'error',
        tags: {
          module: 'create-study',
          action: 'fetchStudyVersionList',
        },
        contexts: {
          study: {
            studyId,
            errorMessage: content?.message,
          },
        },
      })
      dispatch(
        notyActions.showError({
          text: generateNotyMessage(
            content?.message || 'There was an error during fetching study versions list.',
            false,
          ),
        }),
      )
      dispatch(setStudyVersionList([], studyId))
    }
    return dispatch(
      request({
        method: 'GET',
        url: `/control/studies/${studyId}/versions`,
        success: json => {
          if (fetchGeneration !== studyVersionListFetchGeneration) return

          const list = json?.study_versions_info
          dispatch(setStudyVersionList(list, studyId))
          return list
        },
        fail,
        hasLoader: false,
      }),
    )
  }
}

/**
 * After promote/create-version: refresh versions, keep the new id in Redux even if the
 * versions API is briefly stale, then navigate so the study route guard does not treat
 * the new Live/UAT/Dev id as missing and bounce the user back.
 */
const navigateToPromotedStudyVersion = (dispatch, getState, studyId, newVersionId, versionStatus) => {
  if (!newVersionId) return Promise.resolve()

  return dispatch(fetchStudyVersionList(studyId)).then(() => {
    const versions = getState()?.study?.studyVersionList || []
    if (!isStudyVersionInList(versions, newVersionId)) {
      dispatch(
        setStudyVersionList(
          [
            ...(Array.isArray(versions) ? versions : []),
            {
              id: newVersionId,
              version_id: newVersionId,
              version_status: versionStatus,
            },
          ],
          studyId,
        ),
      )
    }
    browserHistory.push(`/studies/${studyId}/${newVersionId}`)
  })
}

export const promoteStudyToUat = (studyId, notes = '') => {
  return (dispatch, getState) => {
    const fail = (_, content) => {
      logSentryEvent({
        message: 'Promote study to UAT failed',
        level: 'error',
        tags: {
          module: 'create-study',
          action: 'promoteStudyToUat',
        },
        contexts: {
          study: {
            studyId,
            errorMessage: content?.message,
          },
        },
      })
      dispatch(
        notyActions.showError({
          text: generateNotyMessage(content?.message || 'There was an error during promoting study to UAT.', false),
        }),
      )
    }
    return dispatch(
      request({
        method: 'PATCH',
        url: `/control/studies/${studyId}/promote/dev_to_uat`,
        body: JSON.stringify({ notes }),
        success: json => {
          return navigateToPromotedStudyVersion(dispatch, getState, studyId, json.new_version_id, 'uat').then(() => {
            dispatch(
              notyActions.showSuccess({
                text: generateNotyMessage('Successfully promoted study to UAT'),
              }),
            )
          })
        },
        fail,
        hasLoader: false,
      }),
    )
  }
}

export const promoteStudyToLive = studyId => {
  return (dispatch, getState) => {
    const fail = (_, content) => {
      logSentryEvent({
        message: 'Promote study to Live failed',
        level: 'error',
        tags: {
          module: 'create-study',
          action: 'promoteStudyToLive',
        },
        contexts: {
          study: {
            studyId,
            errorMessage: content?.message,
          },
        },
      })
      dispatch(
        notyActions.showError({
          text: generateNotyMessage(content?.message || 'There was an error during promoting study to Live.', false),
        }),
      )
    }
    return dispatch(
      request({
        method: 'PATCH',
        url: `/control/studies/${studyId}/promote/uat_to_live`,
        success: json => {
          return navigateToPromotedStudyVersion(dispatch, getState, studyId, json.new_version_id, 'live').then(() => {
            dispatch(
              notyActions.showSuccess({
                text: generateNotyMessage('Successfully promoted study to Live'),
              }),
            )
          })
        },
        fail,
        hasLoader: false,
      }),
    )
  }
}

export const saveStudyVersionNotes = (studyId, notes = '') => {
  return (dispatch, getState) => {
    const studyVersionID = getStudyVersionID(getState()?.location)
    const fail = (_, content) => {
      logSentryEvent({
        message: 'Save study version notes failed',
        level: 'error',
        tags: {
          module: 'create-study',
          action: 'saveStudyVersionNotes',
        },
        contexts: {
          study: {
            studyId,
            studyVersionID,
            errorMessage: content?.message,
          },
        },
      })
      dispatch(
        notyActions.showError({
          text: generateNotyMessage(content?.message || 'There was an error during saving study version notes.', false),
        }),
      )
    }
    return dispatch(
      request({
        method: 'PATCH',
        url: `/control/studies/${studyId}/version/${studyVersionID}`,
        body: JSON.stringify({ notes }),
        success: json => {
          dispatch(
            notyActions.showSuccess({
              text: generateNotyMessage('Successfully saved study version notes'),
            }),
          )
        },
        fail,
        hasLoader: false,
      }),
    )
  }
}

export const editStudyVersionNumber = (studyId, versionId, newVersionNumber) => {
  return dispatch => {
    const fail = (_, content) => {
      logSentryEvent({
        message: 'Edit study version number failed',
        level: 'error',
        tags: {
          module: 'create-study',
          action: 'editStudyVersionNumber',
        },
        contexts: {
          study: {
            studyId,
            versionId,
            errorMessage: content?.message,
          },
        },
      })
      dispatch(
        notyActions.showError({
          text: generateNotyMessage(content?.message || 'There was an error during editing study version.', false),
        }),
      )
    }
    return dispatch(
      request({
        method: 'PATCH',
        url: `/control/studies/${studyId}/version/${versionId}`,
        body: JSON.stringify({ version_display_number: newVersionNumber }),
        success: json => {
          dispatch(
            notyActions.showSuccess({
              text: generateNotyMessage('Successfully edited study version'),
            }),
          )
        },
        fail,
        hasLoader: false,
      }),
    )
  }
}

export const createNewStudyVersion = studyId => {
  return (dispatch, getState) => {
    const fail = (_, content) => {
      logSentryEvent({
        message: 'Create new study version failed',
        level: 'error',
        tags: {
          module: 'create-study',
          action: 'createNewStudyVersion',
        },
        contexts: {
          study: {
            studyId,
            errorMessage: content?.message,
          },
        },
      })
      dispatch(
        notyActions.showError({
          text: generateNotyMessage(content?.message || 'There was an error during creating new study version.', false),
        }),
      )
    }
    return dispatch(
      request({
        method: 'PATCH',
        url: `/control/studies/${studyId}/promote/to_dev`,
        body: JSON.stringify({ notes: '' }),
        success: json => {
          return navigateToPromotedStudyVersion(dispatch, getState, studyId, json.new_version_id, 'dev').then(() => {
            dispatch(
              notyActions.showSuccess({
                text: generateNotyMessage('Successfully created new study version'),
              }),
            )
          })
        },
        fail,
        hasLoader: false,
      }),
    )
  }
}

export const patchStudy = ({ studyId, studyDiff, diffIsConfig = false }) => {
  return dispatch => {
    if (diffIsConfig) {
      return dispatch(
        _saveStudy(
          { ...studyDiff, config: studyDiff.config ? _convertConfigForSave(studyDiff.config) : undefined },
          true,
          studyId,
          diffIsConfig,
        ),
      )
    }
    return dispatch(_saveStudy(studyDiff, true, studyId))
  }
}

export const _deleteStudy = (id, success) => {
  return dispatch => {
    const onConfirm = () => {
      return dispatch(
        request({
          method: 'DELETE',
          url: `/control/studies/${id}`,
          successMessage: 'Study successfully deleted',
          successIcon: 'fas fa-trash-alt',
          hasLoader: true,
          success,
        }),
      )
    }
    dispatch(
      modalActions.openModal({
        onConfirm,
        content: MODAL_CONTENT_MAP.cannotBeUndone,
        className: MODAL_CLASSES_MAP.confirmation,
      }),
    )
  }
}

const deleteStudy = id => dispatch => {
  dispatch(
    _deleteStudy(id, () => {
      browserHistory.push('/studies')
    }),
  )
}

const showPaymentAddSuccessFailNoty = (isFail = false) => dispatch => {
  if (isFail) {
    dispatch(
      notyActions.showError({
        text: generateNotyMessage(`Please check errors before proceeding`, true),
      }),
    )
  } else {
    dispatch(
      notyActions.showSuccess({
        text: generateNotyMessage(`Payment account successfully added`, true),
      }),
    )
  }
}

const validatePayoutCredentials = (credentials, success, fail) => dispatch => {
  dispatch(loadingActions.startLoader(true, 'credentials'))
  return dispatch(
    request({
      method: 'POST',
      url: `/control/validate_credentials`,
      failMessage: 'There was an error validating credentials',
      hasLoader: false,
      success: res => {
        success(res).then(() => {
          dispatch(loadingActions.stopLoader(true, 'credentials'))
        })
      },
      fail: () => {
        fail().then(() => {
          dispatch(loadingActions.stopLoader(true, 'credentials'))
        })
      },
      body: JSON.stringify(credentials),
    }),
  )
}

// Util Functions

const _getDefaultMapProgress = () => {
  return {
    medication_adherence: {
      duration_type: 'DAY',
      duration: 1,
      target_per_duration: 2,
      required_streak: 2,
    },
  }
}

//TODO: remove advanced payouts if payout_types is empty or if rewardType is gems

export const _syncLegacyRewardFieldsFromCycles = config => {
  const uiCycles = config?.cycles
  if (!uiCycles?.length) return config
  const firstCycle = uiCycles[0]
  if (config.reward_goal == null && firstCycle.reward != null && firstCycle.reward !== '') {
    config.reward_goal = firstCycle.reward
  }
  return config
}

/**
 * API persists admin session timeout as `session_timeout`, but the create/edit UI
 * reads/writes `timeout_flag`. Map API → UI so the value is visible when editing.
 */
export const mapAdminSessionTimeoutForUi = adminSecurity => {
  if (!adminSecurity) return adminSecurity
  const { session_timeout: sessionTimeout, timeout_flag: timeoutFlag } = adminSecurity
  if ((timeoutFlag === undefined || timeoutFlag === null || timeoutFlag === '') && sessionTimeout != null) {
    return { ...adminSecurity, [SECURITY_KEY_MAP.timeout_flag]: sessionTimeout }
  }
  return adminSecurity
}

/**
 * Map UI `timeout_flag` back to API `session_timeout` before save.
 */
export const mapAdminSessionTimeoutForSave = adminSecurity => {
  if (!adminSecurity) return adminSecurity
  const next = { ...adminSecurity }
  if (Object.prototype.hasOwnProperty.call(next, SECURITY_KEY_MAP.timeout_flag)) {
    next[SECURITY_KEY_MAP.session_timeout] = next[SECURITY_KEY_MAP.timeout_flag]
    delete next[SECURITY_KEY_MAP.timeout_flag]
  }
  return next
}

const _convertStudyForSave = study => {
  const newStudy = JSON.parse(JSON.stringify(study))
  const convertedModules = Object.keys(newStudy.config.modules)
    .filter(key => study.config.modules[key])
    .map(key => key)
  newStudy.config.modules = convertedModules
  _syncLegacyRewardFieldsFromCycles(newStudy.config)
  if (newStudy.config.reward_goal != null) {
    newStudy.reward_goal = newStudy.config.reward_goal
  }
  requiredFields.forEach(field => {
    if (newStudy.config?.[field] === null) {
      delete newStudy.config[field]
    }
    if (newStudy[field] === null) {
      delete newStudy[field]
    }
  })
  const { config } = newStudy
  const {
    admin_security,
    advanced_payouts,
    cycles,
    has_dynamic_cycles,
    hasAdvancedFeatures,
    medication_adherence,
    participant_data_fields,
    pre_screen_id_enabled,
    session_timeout_seconds,
    subject_id_enabled,
    super_gem_store_payouts,
  } = config

  const isChina = isDeployRegionChina()
  /**
   * SETTING DEFAULT INSIGHTS IN CONFIG
   * If app is deployed in China, we want to hide Insights, so we skip adding
   * an Insights configuration to the config.
   *  */

  if (!isChina) newStudy.config.insights = DEFAULT_INSIGHTS_DASHBOARD_CONFIG
  newStudy.config.consent_2 = { ...(newStudy.config.consent_2 || {}) }
  newStudy.config.allow_auto_recovery_flows = checkIsEmailless(newStudy)
  if (session_timeout_seconds === 0) {
    delete newStudy.config.session_timeout_seconds
  }
  if (medication_adherence) {
    newStudy.config.map_progress = _getDefaultMapProgress()
  }
  /**
   * Ensure participant_data_fields stays in sync with the ID feature flags.
   * If Pre-Screen ID or Subject ID are not enabled, strip their config
   * from participant_data_fields so that Participant creation/edit screens
   * do not render fields that were not selected during study setup.
   */
  if (participant_data_fields) {
    const enabledPreScreen = !!pre_screen_id_enabled
    const enabledSubject = !!subject_id_enabled
    const shouldKeepField = field => {
      const key = field?.metadata?.internal_key
      if (key === 'pre_screen_id') return enabledPreScreen
      if (key === 'subject_id') return enabledSubject
      return true
    }
    newStudy.config.participant_data_fields = {
      mandatory: (participant_data_fields.mandatory || []).filter(shouldKeepField),
      optional: (participant_data_fields.optional || []).filter(shouldKeepField),
      disabled: (participant_data_fields.disabled || []).filter(shouldKeepField),
    }
  }
  if (cycles !== null) {
    const firstCycle = cycles[0]
    const { rewardType } = firstCycle
    if (rewardType === REWARD_TYPE_MAP.gems || !advanced_payouts || !advanced_payouts.payout_types.length) {
      delete newStudy.config.advanced_payouts
    }
    const payoutTypes = advanced_payouts?.payout_types
    const payoutType = payoutTypes?.length > 0 ? payoutTypes[0].type : null
    newStudy.config.cycles = has_dynamic_cycles
      ? generateDynamicCycles(cycles, payoutType)
      : generateCycles(cycles, payoutType)
  } else {
    delete newStudy.config.cycles
  }
  if (!newStudy.config.hasEnforcedLanguage) {
    delete newStudy.config.enforced_language
  }
  if (advanced_payouts && Object.keys(advanced_payouts).includes('hasAdvancedPayouts')) {
    if (!advanced_payouts?.hasAdvancedPayouts) {
      delete newStudy.config.advanced_payouts
    } else {
      delete newStudy.config.advanced_payouts.hasAdvancedPayouts
    }
  }
  if (super_gem_store_payouts && Object.keys(super_gem_store_payouts).includes('hasSuperGemStorePayouts')) {
    if (!super_gem_store_payouts?.hasSuperGemStorePayouts) {
      delete newStudy.config.super_gem_store_payouts
    } else {
      delete newStudy.config.super_gem_store_payouts.hasSuperGemStorePayouts
    }
  }
  if (admin_security) {
    newStudy.config.admin_security = mapAdminSessionTimeoutForSave(newStudy.config.admin_security)
    const pwSecurityArr = Object.keys(newStudy.config.admin_security || {})
    pwSecurityArr.forEach(securityKey => {
      if (!newStudy.config.admin_security[securityKey]) {
        delete newStudy.config.admin_security[securityKey]
      }
      if ([SECURITY_KEY_MAP.lockout_time, SECURITY_KEY_MAP.attemp_num].includes(securityKey)) {
        if (!newStudy.config.admin_security[SECURITY_KEY_MAP.attempt_num]) {
          delete newStudy.config.admin_security.lockout_time
        }
        if (!newStudy.config.admin_security[SECURITY_KEY_MAP.lockout_time]) {
          delete newStudy.config.admin_security.attempt_num
        }
      }
    })
    // if there is no valid values in admin_security, we delete the key
    if (Object.keys(newStudy.config.admin_security || {}).length === 0) delete newStudy.config.admin_security
  }

  delete newStudy.config.hasEnforcedLanguage
  if (!hasAdvancedFeatures) {
    advancedStudyConfigKeys.forEach(key => {
      delete newStudy.config[key]
    })
  }

  delete newStudy.config.hasAdvancedFeatures

  newStudy.config.irt_controlled_visits = getIrtControlledVisitsConfig(newStudy.config)
  syncCaregiversVersionWithStudyPromotion(newStudy.config)

  // For compatibility with studies created in 3.16 and below
  legacyKeys.forEach(key => {
    if (key in newStudy.config) {
      newStudy[key] = newStudy.config[key]
    }
  })
  return newStudy
}

const _convertConfigForSave = config => {
  const newConfig = JSON.parse(JSON.stringify(config))
  if (newConfig.modules) {
    const convertedModules = Object.keys(newConfig.modules)
      .filter(key => newConfig.modules[key])
      .map(key => key)
    newConfig.modules = convertedModules
    _syncLegacyRewardFieldsFromCycles(newConfig)
    const {
      advanced_payouts,
      cycles,
      has_dynamic_cycles,
      hasAdvancedFeatures,
      medication_adherence,
      session_timeout_seconds,
    } = newConfig
    if (session_timeout_seconds === 0) {
      delete newConfig.session_timeout_seconds
    }
    if (medication_adherence) {
      newConfig.map_progress = _getDefaultMapProgress()
    }
    if (cycles !== null) {
      const firstCycle = cycles[0]
      const { rewardType } = firstCycle
      if (rewardType === REWARD_TYPE_MAP.gems || !newConfig.advanced_payouts || !advanced_payouts.payout_types.length) {
        delete newConfig.advanced_payouts
      }
      const payoutTypes = advanced_payouts?.payout_types
      const payoutType = payoutTypes?.length > 0 ? payoutTypes[0].type : null
      newConfig.cycles = has_dynamic_cycles
        ? generateDynamicCycles(cycles, payoutType)
        : generateCycles(cycles, payoutType)
    } else {
      delete newConfig.cycles
    }
    if (!newConfig.hasEnforcedLanguage) {
      delete newConfig.enforced_language
    }
    delete newConfig.hasEnforcedLanguage
    if (!hasAdvancedFeatures) {
      advancedStudyConfigKeys.forEach(key => {
        delete newConfig[key]
      })
    }
    delete newConfig.hasAdvancedFeatures
  }
  if (newConfig.admin_security) {
    newConfig.admin_security = mapAdminSessionTimeoutForSave(newConfig.admin_security)
  }
  newConfig.irt_controlled_visits = getIrtControlledVisitsConfig(newConfig)
  syncCaregiversVersionWithStudyPromotion(newConfig)
  return newConfig
}

export const _formatStudy = (study, dispatch) => {
  const cyclesPath = study.config ? study.config : study
  const cyclesData = cyclesPath.cycles
  if (cyclesData instanceof Array && cyclesData.length > 0 && cyclesData[0].duration) {
    const cyclesInfo = cyclesPath.has_dynamic_cycles ? getDynamicCyclesInfo(cyclesData) : getCyclesInfo(cyclesData)
    study.cycles = cyclesInfo
    if (study.config) {
      study.config.cycles = cyclesInfo
    }
  }
  let { modules } = study
  if (study.config) {
    modules = study.config.modules
  }
  const formattedModules = DEFAULT_STUDY.config.modules
  const moduleKeys = Object.keys(formattedModules)
  moduleKeys.forEach(key => {
    formattedModules[key] = false
  })
  modules.forEach(name => {
    formattedModules[name] = true
  })
  study.modules = formattedModules
  if (study.config) {
    study.config.modules = formattedModules
    study.config.hasAdvancedFeatures = advancedStudyConfigKeys.some(key => study.config[key])
    if (study.config.irt_controlled_visits != null) {
      study.config.irt_controlled_visits = getIrtControlledVisitsConfig(study.config)
    }
    if (study.config.admin_security) {
      study.config.admin_security = mapAdminSessionTimeoutForUi(study.config.admin_security)
    }
  }
  dispatch(loadingActions.stopLoader(true, 'studyConfig'))
}

export const getIsMissingRequiredDynamicCycleFields = cycles => {
  return cycles.some(cycle => {
    return requiredDynamicCycleFields.some(field => !cycle[field] || cycle[field] === 0)
  })
}

const _hasStudyError = study => {
  const { config } = study
  const { super_gems, super_gem_store_payouts } = config
  if (study.name === '' || study.platform_name === '') return true
  const isFixedCycleStudy = !study.config?.has_dynamic_cycles
  if (isFixedCycleStudy) {
    if (requiredCycleFields.some(field => !study.config?.cycles[0][field])) return true
    const studyDurationInDays =
      study.config.cycles[0].durationType === 'weeks'
        ? study.config.cycles[0].studyDuration * 7
        : study.config.cycles[0].studyDuration
    if (studyDurationInDays < study.config.cycles[0].cycleDuration) return true
  } else {
    return getIsMissingRequiredDynamicCycleFields(study.config.cycles)
  }

  if (study.config.hasEnforcedLanguage) {
    if (!study.config.enforced_language.default_language) return true
  }

  const hasEligibility = requiredFields.some(field => !!study.config[field])
  if (hasEligibility) return requiredFields.some(field => !study.config[field])

  // If special rewards (super gems) is selected, ensure either donations or gift cards are toggled
  if (super_gems) {
    const supergemKeys = Object.keys(super_gems)
    if (supergemKeys.length === 0) return true
    if (supergemKeys.includes('donations') && !super_gems.donations && !super_gems.gift_cards) return true
    if (supergemKeys.includes('gift_cards') && !super_gems.donations && !super_gems.gift_cards) return true
  }

  // Check for required gift card credentials and/or required donation info
  if (super_gem_store_payouts) {
    // Check for required gift card credentials
    if (super_gem_store_payouts.payout_types.length === 0) return true
    if (!super_gems.gift_cards) return true
  }
  if (super_gems?.donations) {
    // Check for required donation information if there is donation checked
    const donationInfo = super_gems.donations[0]
    const { summary, detailed_description, value, asset_download_link } = donationInfo
    return !summary || !detailed_description || !value || !asset_download_link
  }

  return false
}

export const uploadCustomLogo = url => ({
  type: UPDATE_STUDY_CUSTOM_LOGO,
  url,
})

export const uploadImage = formData => {
  const success = payload => {
    const { full_image_name } = payload
    return {
      src: full_image_name,
    }
  }
  return dispatch => {
    return dispatch(
      request({
        method: 'POST',
        url: '/control/study_image',
        contentType: 'multipart/form-data',
        body: formData,
        hasLoader: true,
        loadingKey: 'mediaUpload',
        success,
        successMessage: 'Image successfully uploaded.',
        catchMessage: 'There was a problem uploading the image. Please try again later.',
      }),
    )
  }
}

const filterOutEmailField = field => field.type !== 'email'

export const currentStudy = (state = DEFAULT_STUDY, action) => {
  const newState = JSON.parse(JSON.stringify(state))
  switch (action.type) {
    case INITIALIZE_BLANK_STUDY: {
      return action.value
    }
    case TOGGLE_MODULE:
      newState.config.modules = { ...newState.config.modules }
      newState.config.modules[action.key] = !newState.config.modules[action.key]
      return newState
    case UPDATE_STUDY:
      if (action.key instanceof Array) {
        action.key.forEach((key, idx) => {
          if (key === 'cycles') {
            if (action.value instanceof Array) {
              // for dynamic cycles
              newState.config.cycles = action.value[idx]
            } else {
              // for fixed cycles
              newState.config.cycles = [action.value[idx]]
            }
          } else {
            newState.config[key] = action.value[idx]
          }
        })
      } else if (action.key === 'cycles') {
        if (action.value instanceof Array) {
          newState.config.cycles = action.value
        } else {
          newState.config.cycles = [action.value]
        }
      } else if (action.key === 'emailless') {
        const config = newState.config || {}
        const ptpDataFields = config.participant_data_fields || {}
        const caregiversDataFields = config.caregiver_data_fields || {}

        const ptpDataFieldsKeys = Object.keys(ptpDataFields)

        const ptpDisabled = ptpDataFields.disabled || []
        const ptpMandatory = ptpDataFields.mandatory || []
        const cgDisabled = caregiversDataFields.disabled || []
        const cgOptional = caregiversDataFields.optional || []

        if (action.value) {
          config.allow_auto_recovery_flows = true
          ptpDataFields.disabled = [DEFAULT_EMAIL_CONFIG, ...ptpDisabled]
          ptpDataFields.mandatory = ptpMandatory.filter(filterOutEmailField)

          caregiversDataFields.disabled = [...DEFAULT_NAME_CONFIG, ...cgDisabled]
          caregiversDataFields.optional = []
        } else {
          config.allow_auto_recovery_flows = false
          ptpDataFields.disabled = ptpDisabled.filter(filterOutEmailField)

          if (!ptpDataFields.disabled.length && ptpDataFieldsKeys.length === 1) {
            delete newState.participant_data_fields
          }
          ptpDataFields.mandatory = [DEFAULT_EMAIL_CONFIG, ...ptpMandatory]

          const optionalFields = []
          const disabledFields = []

          for (const field of cgDisabled) {
            const key = field?.metadata?.internal_key
              ; (key === 'fname' || key === 'lname' ? optionalFields : disabledFields).push(field)
          }

          caregiversDataFields.disabled = disabledFields
          caregiversDataFields.optional = [...cgOptional, ...optionalFields]
        }

        config.participant_data_fields = ptpDataFields
        config.caregiver_data_fields = caregiversDataFields
        newState.config = config
      } else {
        newState[action.key] = action.value
      }
      return newState
    case UPDATE_CONFIG:
      Object.entries(action.value).forEach(([key, value]) => {
        newState.config[key] = value
      })
      syncCaregiversVersionWithStudyPromotion(newState.config)
      return newState
    case TOGGLE_LANGUAGE: {
      const { enforced_language, hasEnforcedLanguage } = newState.config
      let newLanguages = []
      if (hasEnforcedLanguage) {
        const languageKeys = enforced_language.languages.map(lang => lang.key)
        if (languageKeys.includes(action.language.key)) {
          newLanguages = enforced_language.languages.filter(lang => lang.key !== action.language.key)
          if (action.language.key === enforced_language.default_language) {
            newState.config.enforced_language.default_language = ''
          }
        } else {
          newLanguages = [...enforced_language.languages, action.language]
        }
        newLanguages = newLanguages.sort((a, b) => a.text.localeCompare(b.text))
        newState.config.enforced_language.languages = newLanguages
      }
      return newState
    }
    case UPDATE_CYCLE:
      newState.config.cycles = newState.config.cycles.map((cycle, idx) => {
        if (idx === action.idx) {
          return {
            ...cycle,
            [action.key]: action.value,
          }
        }
        return cycle
      })
      return newState
    case ADD_CYCLE:
      newState.config.cycles = [...newState.config.cycles, newState.config.cycles.slice(-1)[0]]
      return newState
    case DELETE_CYCLE:
      newState.config.cycles = newState.config.cycles.filter((cycle, idx) => idx !== action.idx)
      return newState
    case UPDATE_AUTO_NOTIFICATION: {
      newState.config.auto_notification = { ...newState.config.auto_notification }
      newState.config.auto_notification[action.key] = action.value
      return newState
    }
    case SET_STUDY: {
      return action.study
    }
    case UPDATE_STUDY_CUSTOM_LOGO: {
      newState['custom-logo'] = action.url
      return newState
    }
    default:
      return state
  }
}

const studyVersionTableList = (state = [], action) => {
  switch (action.type) {
    case SET_STUDY_VERSION_LIST:
      return formatStudyVersionList(action.value)
    case RESET_STUDY_VERSION_LIST:
      return []
    default:
      return state
  }
}

const studyVersionList = (state = [], action) => {
  switch (action.type) {
    case SET_STUDY_VERSION_LIST:
      return action.value
    case RESET_STUDY_VERSION_LIST:
      return []
    default:
      return state
  }
}

const studyVersionListStudyId = (state = null, action) => {
  switch (action.type) {
    case SET_STUDY_VERSION_LIST:
      return action.studyId
    case RESET_STUDY_VERSION_LIST:
      return null
    default:
      return state
  }
}

const saveAttempted = (state = false, action) => {
  switch (action.type) {
    case SAVE_ATTEMPTED:
      return true
    case INITIALIZE_BLANK_STUDY:
      return false
    default:
      return state
  }
}

const orphanedParticipants = (state = false, action) => {
  switch (action.type) {
    case SET_ORPHANED_PARTICIPANTS:
      return action.bool
    case INITIALIZE_BLANK_STUDY:
    case SET_STUDY:
      return false
    default:
      return state
  }
}

const insightsLowFunctionalityMode = (state = false, action) => {
  switch (action.type) {
    case SET_INSIGHTS_LOW_FUNCTIONALITY_MODE:
      return true
    case RESET_INSIGHTS_LOW_FUNCTIONALITY_MODE:
      return false
    default:
      return state
  }
}

const createStudyError = (state = '', action) => {
  switch (action.type) {
    case SET_CREATE_STUDY_ERROR:
      return action.error
    case CLEAR_CREATE_STUDY_ERROR:
      return ''
    default:
      return state
  }
}

export const actions = {
  addCycle,
  createStudy,
  deleteCycle,
  deleteStudy,
  fetchLocales,
  fetchStudy,
  patchStudy,
  resetInsightsLowFunctionalityMode,
  resetStudy,
  resetStudyData,
  showPaymentAddSuccessFailNoty,
  toggleLanguage,
  toggleModule,
  updateConfig,
  updateCycle,
  updateRewards,
  updateStudy,
  uploadImage,
  validatePayoutCredentials,
  fetchStudyVersionList,
  promoteStudyToUat,
  promoteStudyToLive,
  saveStudyVersionNotes,
  editStudyVersionNumber,
  createNewStudyVersion,
}

export default combineReducers({
  currentStudy,
  saveAttempted,
  orphanedParticipants,
  locales,
  localesMap,
  insightsLowFunctionalityMode,
  studyVersionTableList,
  studyVersionList,
  studyVersionListStudyId,
  createStudyError,
})
